import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_ORGANIZATION_NATURAL_CAPITAL_ACCOUNT_PATH,
  GetOrganizationNaturalCapitalAccountErrorDto,
  GetOrganizationNaturalCapitalAccountSuccessDto,
} from '@/api/rest/resources/types/organization';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useNCAParentOrganizationId } from '@/pages/shared/hooks/useNCAParentOrganizationId';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

type SuccessDto = GetOrganizationNaturalCapitalAccountSuccessDto;
type ErrorDto = GetOrganizationNaturalCapitalAccountErrorDto;

export const useNaturalCapitalAccount = (organizationIdArg?: string, plotGroupIdArg?: string | null) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  const plotGroupId = usePlotGroupId();
  const ncaParentId = useNCAParentOrganizationId();

  assertIsDefined(organizationId, 'organizationId');

  const pathParams = { organization_id: organizationId };
  const queryParams = {
    plot_group_id: plotGroupIdArg ?? plotGroupId,
    ...(ncaParentId ? { nca_parent_id: ncaParentId } : {}),
  };

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_ORGANIZATION_NATURAL_CAPITAL_ACCOUNT_PATH, pathParams, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', 'natural-capital-account', pathParams, queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
